import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CdkDragMove } from '@angular/cdk/drag-drop';
import { debounceTime } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormControl, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TooltipDirective } from '../../components/tooltip.directive';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';

import { KeyValue } from '@angular/common';
import { faEdit, faTrashAlt, faClone } from '@fortawesome/free-regular-svg-icons';
import { faInfo, faPlus, faTimes, faChevronUp, faChevronDown, faGrip } from '@fortawesome/free-solid-svg-icons';

import { FormInputComponent } from '../../components/form-input/form-input.component';
import { FormTextareaComponent } from '../../components/form-textarea/form-textarea.component';
import { ButtonComponent } from '../../components/button/button.component';
import { CheckboxComponent } from '../../components/checkbox/checkbox.component';
import { SectionModalComponent } from '../../components/section-modal/section-modal.component';

export type ReferenceImportMode = 'section_per_reference' | 'merge';

export interface ReferenceImportItem {
  id: string | number;
  name: string;
  data?: Record<string, any> | null;
}

@Component({
  selector: 'app-models-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    FontAwesomeModule,
    TooltipDirective,
    DragDropModule,
    FormInputComponent,
    FormTextareaComponent,
    ButtonComponent,
    CheckboxComponent,
    SectionModalComponent,
  ],
  templateUrl: './models-form.component.html',
  styleUrls: ['./models-form.component.scss'],
})
export class ModelsFormComponent {
  @Input() descriptionsForm = this.fb.group({}) as FormGroup;
  @Input() formatForm = this.fb.group({}) as FormGroup;
  @Input() showSaveFormatOption: boolean = true;
  @Input() rulesMode: boolean = false;
  @Input() formType: 'desired' | 'generalRules' | 'generalQuestions' = 'desired';
  @Input() mentionData: string[] = [];
  @Input() initialValue: any = null;
  @Output() mentionDataChange = new EventEmitter<string[]>();
  @Output() submit = new EventEmitter<void>();
  @Output() valueChange = new EventEmitter<any>();

  sectionModal = {
    active: false,
    close: () => {
      this.sectionModalGroup.reset();
      this.sectionModal.active = false;
    },
    open: () => (this.sectionModal.active = true),
  };

  sectionModalGroup: FormGroup = this.fb.group({
    id: ['', Validators.required],
    description: ['', Validators.required],
  });

  editSectionModalGroup: FormGroup = this.fb.group({
    id: ['', Validators.required],
    description: ['', Validators.required],
  });

  editSectionModal = {
    active: false,
    editedSectionKey: '' as string,
    close: () => {
      this.editSectionModalGroup.reset();
      this.editSectionModal.active = false;
    },
    open: (sectionKey: string) => {
      this.editSectionModal.editedSectionKey = sectionKey;
      const section = this.descriptionsForm.controls[sectionKey] as FormGroup;
      const currentId = section.controls['id']?.value || '';
      const currentDescription = section.controls['description']?.value || '';
      this.editSectionModalGroup.patchValue({
        id: currentId,
        description: currentDescription,
      });
      this.editSectionModal.active = true;
    },
  };

  icons = {
    plus: faPlus,
    info: faInfo,
    edit: faEdit,
    delete: faTrashAlt,
    duplicate: faClone,
    cancel: faTimes,
    expand: faChevronUp,
    collapse: faChevronDown,
    grip: faGrip,
  };

  private autoScrollMargin = 60;
  private autoScrollSpeed = 18;
  public sectionExpanded: { [sectionKey: string]: boolean } = {};

  constructor(
    private fb: FormBuilder,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.initialValue) {
      this.applyInitialValue(this.initialValue);
    }

    this.descriptionsForm.valueChanges
      .pipe(debounceTime(0))
      .subscribe(() => {
        this.emitCurrentValue();
        if (this.formType === 'desired') {
          this.emitMentionDataFromForm();
        }
      });

    this.registerAllSectionListeners();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialValue'] && !changes['initialValue'].firstChange) {
      const value = changes['initialValue'].currentValue;
      this.applyInitialValue(value);

    }
    
    if (changes['formType'] && !changes['formType'].firstChange) {
      this.applyInitialValue(this.initialValue);
    }
  }

  private applyInitialValue(value: any): void {
    if (!this.descriptionsForm) return;

    this.sectionExpanded = {};
    Object.keys(this.descriptionsForm.controls).forEach((key) => {
      this.descriptionsForm.removeControl(key, { emitEvent: false });
    });

    if (!value || typeof value !== 'object') {
      this.descriptionsForm.updateValueAndValidity({ emitEvent: false });
      return;
    }

    if (this.formType === 'generalRules') {
      Object.keys(value).forEach((key) => {
        this.descriptionsForm.addControl(
          key,
          this.fb.control(value[key] ?? ''),
          { emitEvent: false }
        );
      });
    } else if (this.formType === 'generalQuestions') {
      Object.keys(value).forEach((key) => {
        const q = value[key] || {};
        this.descriptionsForm.addControl(
          key,
          this.fb.group(
            {
              id: [q.id ?? ''],
              description: [q.description ?? ''],
            },
            { validators: [Validators.required] }
          ),
          { emitEvent: false }
        );
      });
    } else {
      Object.keys(value).forEach((key) => {
        const item = value[key] || {};
        const groupConfig: any = {
          id: [
            item.id ?? '',
            [Validators.required, Validators.pattern(/^[a-z0-9_]+$/)],
          ],
          description: [item.description ?? ''],
          format: [item.format ?? ''],
          showExtra: [item.showExtra ?? false],
        };

        let childrenGroup: FormGroup | null = null;

        if (item.children && typeof item.children === 'object') {
          childrenGroup = this.fb.group({});

          Object.keys(item.children).forEach((childKey) => {
            const child = item.children[childKey] || {};
            const childGroup = this.fb.group({
              id: [
                child.id ?? '',
                [Validators.required, Validators.pattern(/^[a-z0-9_.]+$/)],
              ],
              description: [child.description ?? ''],
              format: [child.format ?? ''],
              showExtra: [child.showExtra ?? false],
            });
            childrenGroup!.addControl(childKey, childGroup);
          });

          groupConfig.children = childrenGroup;
        }

        const group = this.fb.group(groupConfig);
        this.descriptionsForm.addControl(key, group, { emitEvent: false });
      });

      this.registerAllSectionListeners();
    }
    this.descriptionsForm.updateValueAndValidity({ emitEvent: false });
    this.emitCurrentValue();
    
    if (this.formType === 'desired') {
      setTimeout(() => this.emitMentionDataFromForm());
    }
  }

  onDragMoved(event: CdkDragMove<any>, containerRef?: ElementRef | HTMLElement) {
    let container: HTMLElement | undefined;
    if (containerRef instanceof ElementRef) {
      container = containerRef.nativeElement;
    } else if (containerRef instanceof HTMLElement) {
      container = containerRef;
    }
    if (!container) return;
    const { top, bottom } = container.getBoundingClientRect();
    const mouseY = event.pointerPosition.y;
    const scrollTop = container.scrollTop;
    const maxScroll = container.scrollHeight - container.clientHeight;

    if (mouseY - top < this.autoScrollMargin && scrollTop > 0) {
      container.scrollTop = Math.max(0, scrollTop - this.autoScrollSpeed);
    }
    else if (bottom - mouseY < this.autoScrollMargin && scrollTop < maxScroll) {
      container.scrollTop = Math.min(maxScroll, scrollTop + this.autoScrollSpeed);
    }
  }

  onSubmit(): void {
    this.submit.emit();
  }

  sortZero(): number {
    return 0;
  }

  private emitCurrentValue(): void {
    const value = this.descriptionsForm.getRawValue();
    this.valueChange.emit(value);
  }

  public get descriptionsFormKeys(): string[] {
    return Object.keys(this.descriptionsForm.controls);
  }

  public keyValueComparator = (
    a: KeyValue<string, any>,
    b: KeyValue<string, any>
  ): number => {
    const keys = this.descriptionsFormKeys;
    return keys.indexOf(a.key) - keys.indexOf(b.key);
  };

  public childrenKeys(sectionKey: string): string[] {
    const grp = this.descriptionsForm.get(sectionKey)?.get('children') as FormGroup;
    return grp ? Object.keys(grp.controls) : [];
  }

  private generateUniqueKey(baseKey: string, existing: Record<string, any>): string {
    let suffix = 'copy';
    let candidate = `${baseKey}_${suffix}`;
    let counter = 1;

    while (Object.prototype.hasOwnProperty.call(existing, candidate)) {
      counter += 1;
      candidate = `${baseKey}_${suffix}${counter}`;
    }

    return candidate;
  }

  private cloneWithoutIds(item: any): any {
    const cloned = JSON.parse(JSON.stringify(item || {}));

    if (cloned && typeof cloned === 'object') {
      if ('id' in cloned) {
        cloned.id = '';
      }

      if (cloned.children && typeof cloned.children === 'object') {
        Object.keys(cloned.children).forEach((childKey) => {
          if (cloned.children[childKey] && 'id' in cloned.children[childKey]) {
            cloned.children[childKey].id = '';
          }
        });
      }
    }

    return cloned;
  }

  duplicateField(key: string): void {
    if (this.formType !== 'desired') return;

    const currentValue = this.descriptionsForm.getRawValue();
    const source = currentValue[key];
    if (!source) return;

    const clone = this.cloneWithoutIds(source);
    const newKey = this.generateUniqueKey(key, currentValue);

    const entries = Object.entries(currentValue) as [string, any][];
    const newEntries: [string, any][] = [];

    for (const [k, v] of entries) {
      newEntries.push([k, v]);
      if (k === key) {
        newEntries.push([newKey, clone]);
      }
    }

    const nextValue: Record<string, any> = {};
    newEntries.forEach(([k, v]) => {
      nextValue[k] = v;
    });

    this.applyInitialValue(nextValue);
  }

  duplicateSection(sectionKey: string): void {
    this.duplicateField(sectionKey);
  }

  duplicateChildField(sectionKey: string, childKey: string): void {
    if (this.formType !== 'desired') return;

    const currentValue = this.descriptionsForm.getRawValue();
    const section = currentValue[sectionKey];
    if (!section || !section.children) return;

    const children = section.children as Record<string, any>;
    const sourceChild = children[childKey];
    if (!sourceChild) return;

    const clone = this.cloneWithoutIds(sourceChild);
    const newChildKey = this.generateUniqueKey(childKey, children);

    const childEntries = Object.entries(children) as [string, any][];
    const newChildEntries: [string, any][] = [];

    for (const [k, v] of childEntries) {
      newChildEntries.push([k, v]);
      if (k === childKey) {
        newChildEntries.push([newChildKey, clone]);
      }
    }

    const nextChildren: Record<string, any> = {};
    newChildEntries.forEach(([k, v]) => {
      nextChildren[k] = v;
    });

    const nextValue = JSON.parse(JSON.stringify(currentValue));
    if (!nextValue[sectionKey]) return;
    nextValue[sectionKey].children = nextChildren;

    this.applyInitialValue(nextValue);
  }

  isSection(controlKey: string): boolean {
    const ctrl = this.descriptionsForm.get(controlKey);
    if (!(ctrl instanceof FormGroup)) return false;
    if (ctrl.get('children') instanceof FormGroup) return true;
    return false;
  }

  isSectionExpanded(key: string): boolean {
    return !!this.sectionExpanded[key];
  }

  toggleSection(key: string): void {
    this.sectionExpanded[key] = !this.sectionExpanded[key];
  }

  getChildrenCount(key: string): string {
    const grp = this.descriptionsForm.get(key)?.get('children') as FormGroup;
    const count = grp ? Object.keys(grp.controls).length : 0;
    if (count === 0) return 'Nenhum campo';
    return count === 1 ? '1 Campo' : `${count} Campos`;
  }

  fc(control: FormControl | AbstractControl): FormControl {
    return control as FormControl;
  }

  fg(control: FormControl | AbstractControl): FormGroup {
    return control as FormGroup;
  }

  getControl(control: AbstractControl | null): FormControl {
    return control ? (control as FormControl) : this.fb.control('');
  }

  getFormGroup(control: AbstractControl | null): FormGroup {
    return control ? (control as FormGroup) : this.fb.group({});
  }

  private generateIdFromDescription(description: string, auto: boolean = true): string {
    if (!description) {
      return '';
    }
    let id = description.trim().toLowerCase();
    id = id.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    id = id.replace(/\s+/g, '_');
    id = id.replace(/[^a-z0-9_]/g, '');
    if (auto && id.length > 25) {
      id = id.substring(0, 25);
    }
    return id;
  }

  public importReferences(
    references: ReferenceImportItem[],
    mode: ReferenceImportMode = 'section_per_reference'
  ): { referencesImported: number; fieldsCreated: number } {
    if (this.formType !== 'desired') {
      return { referencesImported: 0, fieldsCreated: 0 };
    }

    const current = this.descriptionsForm.getRawValue() || {};
    const nextValue: Record<string, any> = JSON.parse(JSON.stringify(current));
    const usedIds = this.collectUsedIds(nextValue);

    let referencesImported = 0;
    let fieldsCreated = 0;

    for (const reference of references || []) {
      const data = reference?.data && typeof reference.data === 'object' ? reference.data : {};
      const keys = Object.keys(data);
      if (!keys.length) {
        continue;
      }

      referencesImported += 1;

      if (mode === 'section_per_reference') {
        const sectionBase = this.generateIdFromDescription(reference.name || 'reference') || 'reference';
        const sectionId = this.getUniqueRootId(sectionBase, usedIds);

        const sectionKey = this.getNextObjectKey(nextValue, 'd');
        const sectionChildren: Record<string, any> = {};

        for (const key of keys) {
          const childBase = this.generateIdFromDescription(key || 'field') || 'field';
          const childId = this.getUniqueChildId(sectionId, childBase, usedIds);
          const childKey = this.getNextObjectKey(sectionChildren, 'c');

          sectionChildren[childKey] = {
            id: childId,
            description: key,
            format: '',
            showExtra: false,
          };
          fieldsCreated += 1;
        }

        nextValue[sectionKey] = {
          id: sectionId,
          description: reference.name || sectionId,
          format: '',
          children: sectionChildren,
        };
        continue;
      }

      for (const key of keys) {
        const fieldBase = this.generateIdFromDescription(key || 'field') || 'field';
        const fieldId = this.getUniqueRootId(fieldBase, usedIds);
        const fieldKey = this.getNextObjectKey(nextValue, 'd');

        nextValue[fieldKey] = {
          id: fieldId,
          description: key,
          format: '',
          showExtra: false,
        };
        fieldsCreated += 1;
      }
    }

    if (fieldsCreated > 0) {
      this.applyInitialValue(nextValue);
    }

    return { referencesImported, fieldsCreated };
  }

  private collectUsedIds(rawValue: Record<string, any>): Set<string> {
    const usedIds = new Set<string>();

    Object.values(rawValue || {}).forEach((item: any) => {
      const rootId = String(item?.id || '').trim();
      if (rootId) {
        usedIds.add(rootId);
      }

      if (item?.children && typeof item.children === 'object') {
        Object.values(item.children).forEach((child: any) => {
          const childId = String(child?.id || '').trim();
          if (childId) {
            usedIds.add(childId);
          }
        });
      }
    });

    return usedIds;
  }

  private getUniqueRootId(baseId: string, usedIds: Set<string>): string {
    const safeBase = this.generateIdFromDescription(baseId || 'field') || 'field';
    let candidate = safeBase;
    let suffix = 2;

    while (usedIds.has(candidate)) {
      candidate = `${safeBase}_${suffix}`;
      suffix += 1;
    }

    usedIds.add(candidate);
    return candidate;
  }

  private getUniqueChildId(sectionId: string, baseChildId: string, usedIds: Set<string>): string {
    const safeBase = this.generateIdFromDescription(baseChildId || 'field') || 'field';
    let childCandidate = safeBase;
    let fullCandidate = `${sectionId}.${childCandidate}`;
    let suffix = 2;

    while (usedIds.has(fullCandidate)) {
      childCandidate = `${safeBase}_${suffix}`;
      fullCandidate = `${sectionId}.${childCandidate}`;
      suffix += 1;
    }

    usedIds.add(fullCandidate);
    return fullCandidate;
  }

  private getNextObjectKey(existing: Record<string, any>, prefix: string): string {
    const normalizedPrefix = `${prefix}_`;
    const existingIndexes = Object.keys(existing || {})
      .filter((name) => name.startsWith(normalizedPrefix))
      .map((name) => Number(name.replace(normalizedPrefix, '')))
      .filter((value) => !Number.isNaN(value));

    const nextIndex = existingIndexes.length ? Math.max(...existingIndexes) + 1 : 0;
    return `${normalizedPrefix}${nextIndex}`;
  }

  removeDescription(
    controlKey: string,
    options: {
      sectionKey?: string;
      shouldConfirm?: boolean;
    } = { sectionKey: undefined, shouldConfirm: false }
  ): void {
    this.translateService
      .get('MESSAGE.WISH_TO_DELETE')
      .subscribe((message) => {
        if (options.shouldConfirm) {
          const confirmed = confirm(message);
          if (!confirmed) return;
        }

        let group: FormGroup;
        if (options.sectionKey) {
          const section = this.descriptionsForm.get(options.sectionKey) as FormGroup;
          group =
            (section.get('descriptions') as FormGroup) ??
            (section.get('children') as FormGroup);
        } else {
          group = this.descriptionsForm;
        }
        if (group && group.contains(controlKey)) {
          group.removeControl(controlKey);
          this.emitCurrentValue();
        }
      });
  }

  addNewRuleField(): void {
    const controlName = this.getNewInputName();
    this.descriptionsForm.addControl(
      controlName,
      this.fb.control('', Validators.required)
    );
    this.emitCurrentValue();
  }

  addNewQuestionField(): void {
    const controlName = this.getNewInputName(this.descriptionsForm, 'q');
    const newQuestion = this.fb.group({
      id: [
        this.generateIdFromDescription(''),
        [Validators.required, Validators.pattern(/^[a-z0-9_]+$/)],
      ],
      description: ['', Validators.required],
    });
    newQuestion.controls.description.valueChanges.subscribe(
      (newDesc: string | null) => {
        if (newQuestion.controls.id.pristine) {
          const generated = this.generateIdFromDescription(newDesc ?? '', true);
          newQuestion.controls.id.setValue(generated, { emitEvent: false });
        }
      }
    );
    newQuestion.controls.id.valueChanges.subscribe((val: string | null) => {
      val = val || '';
      const auto = newQuestion.controls.id.pristine;
      const formatted = this.generateIdFromDescription(val, auto);
      if (formatted !== val) {
        newQuestion.controls.id.setValue(formatted, { emitEvent: false });
      }
    });
    this.descriptionsForm.addControl(controlName, newQuestion);
    this.emitCurrentValue();
  }

  addNewFormat2Field(sectionKey?: string): void {
    if (!sectionKey) {
      const controlName = this.getNewInputName(this.descriptionsForm, 'd');
      const newField = this.fb.group({
        id: [
          this.generateIdFromDescription(''),
          [Validators.required, Validators.pattern(/^[a-z0-9_]+$/)],
        ],
        description: [''],
        format: [''],
        showExtra: [false],
      });
      newField
        .get('description')!
        .valueChanges.subscribe((desc: string | null) => {
          if (newField.get('id')!.pristine) {
            const gen = this.generateIdFromDescription(desc ?? '', true);
            newField.get('id')!.setValue(gen, { emitEvent: false });
          }
        });
      newField.get('id')!.valueChanges.subscribe((val: string | null) => {
        const clean = this.generateIdFromDescription(
          val ?? '',
          newField.get('id')!.pristine
        );
        if (clean !== val) newField.get('id')!.setValue(clean, { emitEvent: false });
      });
      this.descriptionsForm.addControl(controlName, newField);
      this.emitCurrentValue();
      return;
    }

    const sectionGroup = this.descriptionsForm.get(sectionKey) as FormGroup;
    if (!sectionGroup) return;
    const childrenGroup = sectionGroup.get('children') as FormGroup;
    if (!childrenGroup) return;

    const sectionId: string = sectionGroup.get('id')?.value || '';
    const controlName = this.getNewInputName(childrenGroup, 'd');

    const newChild = this.fb.group({
      id: [
        `${sectionId}.`,
        [Validators.required, Validators.pattern(/^[a-z0-9_.]+$/)],
      ],
      description: [''],
      format: [''],
      showExtra: [false],
    });
    newChild
      .get('description')!
      .valueChanges.subscribe((desc: string | null) => {
        if (newChild.get('id')!.pristine) {
          const raw = this.generateIdFromDescription(desc ?? '', true);
          newChild
            .get('id')!
            .setValue(`${sectionId}.${raw}`, { emitEvent: false });
        }
      });
    newChild.get('id')!.valueChanges.subscribe((val: string | null) => {
      val = val ?? '';
      const part = val.split('.').pop() || '';
      const clean = this.generateIdFromDescription(
        part,
        newChild.get('id')!.pristine
      );
      const rebuilt = `${sectionId}.${clean}`;
      if (rebuilt !== val)
        newChild.get('id')!.setValue(rebuilt, { emitEvent: false });
    });

    childrenGroup.addControl(controlName, newChild);
    this.sectionExpanded[sectionKey] = true;
    this.emitCurrentValue();
  }

  addSectionFormat2(): void {
    const sectionDescriptionText =
      this.sectionModalGroup.controls['description'].value;
    if (!sectionDescriptionText?.trim()) return;
    const sectionId = this.generateIdFromDescription(sectionDescriptionText);

    const childrenGroup = this.fb.group({});
    const initialChildKey = this.getNewInputName(childrenGroup, 'child');
    const childGroup = this.fb.group({
      id: [
        `${sectionId}.`,
        [Validators.required, Validators.pattern(/^[a-z0-9_.]+$/)],
      ],
      description: [''],
      format: [''],
      showExtra: [false],
    });
    childGroup.controls['description'].valueChanges.subscribe(
      (newDesc: string | null) => {
        if (childGroup.controls['id'].pristine) {
          const raw = this.generateIdFromDescription(newDesc ?? '', true);
          childGroup.controls['id'].setValue(`${sectionId}.${raw}`, {
            emitEvent: false,
          });
        }
      }
    );
    childGroup.controls['id'].valueChanges.subscribe((val: string | null) => {
      const part = (val ?? '').split('.').pop() || '';
      const clean = this.generateIdFromDescription(
        part,
        childGroup.controls['id'].pristine
      );
      const rebuilt = `${sectionId}.${clean}`;
      if (rebuilt !== val) {
        childGroup.controls['id'].setValue(rebuilt, { emitEvent: false });
      }
    });

    childrenGroup.addControl(initialChildKey, childGroup);

    const sectionGroup = this.fb.group({
      id: [
        sectionId,
        [Validators.required, Validators.pattern(/^[a-z0-9_]+$/)],
      ],
      description: [sectionDescriptionText],
      format: [''],
      children: childrenGroup,
    });

    this.attachSectionListeners(sectionGroup);

    const rootKey = this.getNewInputName(this.descriptionsForm, 'd');
    this.descriptionsForm.addControl(rootKey, sectionGroup);
    this.sectionExpanded[rootKey] = true;
    this.sectionModalGroup.reset();
    this.sectionModal.active = false;
    this.emitCurrentValue();
  }

  editSectionName(): void {
    const key = this.editSectionModal.editedSectionKey;
    const sectionGroup = this.descriptionsForm.controls[key] as FormGroup;

    const newDescription =
      this.editSectionModalGroup.controls['description'].value;
    const newId = this.generateIdFromDescription(newDescription);

    sectionGroup.controls['id'].setValue(newId, { emitEvent: false });
    sectionGroup.controls['description'].setValue(newDescription, {
      emitEvent: false,
    });

    const children = sectionGroup.controls['children'] as FormGroup;
    Object.values(children.controls).forEach((childCtrl) => {
      const childGroup = childCtrl as FormGroup;
      const raw =
        ((childGroup.controls['id'].value as string) || '')
          .split('.')
          .pop() || '';
      childGroup.controls['id'].setValue(`${newId}.${raw}`, {
        emitEvent: false,
      });
    });

    this.editSectionModal.close();
  }

  toggleShowExtra(key: string): void {
    const control = this.getControl(
      (this.descriptionsForm.controls[key] as FormGroup).get('showExtra')
    );
    let currentValue = control.value;
    if (
      currentValue === '' ||
      currentValue === null ||
      currentValue === undefined
    ) {
      currentValue = false;
    }
    control.patchValue(!currentValue);
  }

  private registerAllSectionListeners() {
    Object.values(this.descriptionsForm.controls).forEach((ctrl) => {
      if (ctrl instanceof FormGroup) {
        const section = ctrl as FormGroup;
        const children = section.get('children');
        if (children && children instanceof FormGroup) {
          this.attachSectionListeners(section);
        }
      }
    });
  }

  private attachSectionListeners(sectionGroup: FormGroup) {
    const descCtrl = sectionGroup.get('description');
    const idCtrl = sectionGroup.get('id');
    const children = sectionGroup.get('children') as FormGroup | null;

    if (!descCtrl || !idCtrl || !children) return;

    descCtrl.valueChanges.subscribe((newDesc: string) => {
      const newId = this.generateIdFromDescription(newDesc);
      idCtrl.setValue(newId, { emitEvent: false });
    });

    idCtrl.valueChanges.subscribe((newId: string) => {
      Object.values(children.controls).forEach((childCtrl) => {
        const childGroup = childCtrl as FormGroup;
        const raw =
          ((childGroup.get('id')?.value as string) || '')
            .split('.')
            .pop() || '';
        childGroup.get('id')?.setValue(`${newId}.${raw}`, {
          emitEvent: false,
        });
      });
    });
  }

  private reorderGroup(group: FormGroup, orderedKeys: string[]) {
    const store: Record<string, AbstractControl> = {};
    orderedKeys.forEach((k) => (store[k] = group.controls[k]));
    Object.keys(group.controls).forEach((k) => group.removeControl(k));
    orderedKeys.forEach((k) => group.addControl(k, store[k]));
    group.updateValueAndValidity();
  }

  private moveControl(
    fromGroup: FormGroup,
    controlKey: string,
    toGroup: FormGroup,
    toIndex: number
  ) {
    const ctrl = fromGroup.controls[controlKey];
    fromGroup.removeControl(controlKey);
    const keys = Object.keys(toGroup.controls);
    const newOrder = [
      ...keys.slice(0, toIndex),
      controlKey,
      ...keys.slice(toIndex),
    ];
    const store: Record<string, AbstractControl> = {};
    newOrder.forEach(
      (k) => (store[k] = k === controlKey ? ctrl : toGroup.controls[k])
    );
    Object.keys(toGroup.controls).forEach((k) => toGroup.removeControl(k));
    newOrder.forEach((k) => toGroup.addControl(k, store[k]));
    toGroup.updateValueAndValidity();
  }

  private reassignIdPrefix(
    grp: FormGroup,
    key: string,
    newPrefix: string
  ) {
    const idCtrl = (grp.get(key) as FormGroup).get('id')!;
    const raw = (idCtrl.value as string).split('.').pop();
    idCtrl.setValue(newPrefix ? `${newPrefix}.${raw}` : raw, {
      emitEvent: false,
    });
  }

  dropFormat2(event: CdkDragDrop<any>) {
    const prevId = event.previousContainer.id;
    const currId = event.container.id;
    const data = event.item.data as string | { key: string; section: string };

    const isSectionKey = (k: string) =>
      this.descriptionsForm.get(k) instanceof FormGroup &&
      !!this.descriptionsForm.get(k)!.get('children');

    if (prevId === currId) {
      if (prevId === 'rootList') {
        const keys = this.descriptionsFormKeys;
        moveItemInArray(keys, event.previousIndex, event.currentIndex);
        this.reorderGroup(this.descriptionsForm, keys);
      } else {
        const secKey = prevId.replace('secList_', '');
        const grp = this.descriptionsForm
          .get(secKey)!
          .get('children') as FormGroup;
        const keys = this.childrenKeys(secKey);
        moveItemInArray(keys, event.previousIndex, event.currentIndex);
        this.reorderGroup(grp, keys);
      }
      return;
    }

    if (prevId === 'rootList' && currId.startsWith('secList_')) {
      const key = data as string;
      if (isSectionKey(key)) {
        alert(
          this.translateService.instant(
            'DESCRIPTIONS_FORM.CANNOT_NEST_SECTION'
          )
        );
        return;
      }
      const secKey = currId.replace('secList_', '');
      const children = this.descriptionsForm
        .get(secKey)!
        .get('children') as FormGroup;
      this.moveControl(
        this.descriptionsForm,
        key,
        children,
        event.currentIndex
      );
      this.reassignIdPrefix(
        children,
        key,
        this.descriptionsForm.get(secKey)!.get('id')!.value
      );
      return;
    }

    if (prevId.startsWith('secList_') && currId === 'rootList') {
      const { key, section: prevSection } = data as {
        key: string;
        section: string;
      };
      const children = this.descriptionsForm
        .get(prevSection)!
        .get('children') as FormGroup;
      this.moveControl(
        children,
        key,
        this.descriptionsForm,
        event.currentIndex
      );
      this.reassignIdPrefix(this.descriptionsForm, key, '');
      return;
    }

    if (prevId.startsWith('secList_') && currId.startsWith('secList_')) {
      const { key, section: prevSection } = data as {
        key: string;
        section: string;
      };
      if (isSectionKey(key)) {
        alert(
          this.translateService.instant(
            'DESCRIPTIONS_FORM.CANNOT_NEST_SECTION'
          )
        );
        return;
      }
      const fromSection = prevId.replace('secList_', '');
      const toSection = currId.replace('secList_', '');
      const fromChildren = this.descriptionsForm
        .get(fromSection)!
        .get('children') as FormGroup;
      const toChildren = this.descriptionsForm
        .get(toSection)!
        .get('children') as FormGroup;
      this.moveControl(fromChildren, key, toChildren, event.currentIndex);
      this.reassignIdPrefix(
        toChildren,
        key,
        this.descriptionsForm.get(toSection)!.get('id')!.value
      );
      return;
    }
  }

  public getChildShowExtra(sectionKey: string, childKey: string): boolean {
    const child = this.descriptionsForm
      .get(sectionKey)!
      .get('children')! as FormGroup;
    return !!child.controls[childKey].get('showExtra')!.value;
  }

  public toggleChildShowExtra(sectionKey: string, childKey: string): void {
    const child = this.descriptionsForm
      .get(sectionKey)!
      .get('children')! as FormGroup;
    const ctrl = child.controls[childKey].get('showExtra') as FormControl;
    ctrl.patchValue(!ctrl.value);
  }

  public get rootConnectedTo(): string[] {
    return [
      'rootList',
      ...this.descriptionsFormKeys
        .filter((key) => this.isSection(key))
        .map((key) => `secList_${key}`),
    ];
  }

  public get allDropLists(): string[] {
    return [
      'rootList',
      ...this.descriptionsFormKeys
        .filter((k) => this.isSection(k))
        .map((k) => `secList_${k}`),
    ];
  }

  public childConnectedTo(sectionKey: string): string[] {
    return [
      'rootList',
      ...this.descriptionsFormKeys
        .filter((key) => this.isSection(key) && key !== sectionKey)
        .map((key) => `secList_${key}`),
    ];
  }

  public canEnterSection = () => {
    return true;
  };

  private getNewInputName(
    sectionGroup: FormGroup = this.descriptionsForm,
    sectionPrefix?: string
  ): string {
    const prefix =
      (sectionPrefix || (this.formType === 'generalRules' ? 'r' : 'd')) + '_';
    const existentNames = Object.keys(sectionGroup.controls)
      .filter((name) => name.startsWith(prefix))
      .map((name) => {
        const num = Number(name.replace(prefix, ''));
        return isNaN(num) ? 0 : num;
      });
    const newIndex = existentNames.length
      ? Math.max(...existentNames) + 1
      : 0;
    return prefix + newIndex;
  }

  private emitMentionDataFromForm(): void {
    if (this.formType !== 'desired') return;

    const ids: string[] = [];

    Object.values(this.descriptionsForm.controls).forEach(ctrl => {
      const group = ctrl as FormGroup;
      const idCtrl = group.get('id');
      if (idCtrl && idCtrl.value) {
        ids.push(String(idCtrl.value));
      }
      const children = group.get('children') as FormGroup | null;
      if (children) {
        Object.values(children.controls).forEach(childCtrl => {
          const childGroup = childCtrl as FormGroup;
          const childIdCtrl = childGroup.get('id');
          if (childIdCtrl && childIdCtrl.value) {
            ids.push(String(childIdCtrl.value));
          }
        });
      }
    });
    const uniqueIds = Array.from(new Set(ids));
    this.mentionDataChange.emit(uniqueIds);
  }
}
